/*
 * Lemonade Training Oy, copy rights, all right reserved.
 * Booking System Firebase Callable Function Wrappers
 */

import { httpsCallable } from "firebase/functions";
import { v4 as uuidv4 } from "uuid";
import { firebaseFunctions } from "../store/firebaseApp";
import type {
  ClassType,
  Class,
  ClassBooking,
  ClassWaitlist,
  CancelClassRequest,
  CancelClassResponse,
  UpdateClassRequest,
  UpdateClassResponse,
  BookClassRequest,
  BookClassResponse,
  CancelBookingRequest,
  CancelBookingResponse,
  GetClassmatesRequest,
  GetClassmatesResponse,
  GetClassTypesRequest,
  MoveBookingRequest,
  MoveBookingResponse,
  PromoteFromWaitlistResponse,
  ReactivateClassTypeRequest,
  ReactivateClassTypeResponse,
} from "@lemonadetraining/contracts";

/* =======================================
 * Class Type Management Functions
 * ====================================== */

/**
 * Create a new class type
 *
 * @param data - Class type creation data
 * @param data.allowedMembershipProductIDs - Optional array of membership IDs. If null, undefined, or empty array, all members can book.
 * @param data.isInternalByDefault - If true, classes created from this type are hidden from members by default (default: false)
 */
export const createClassType = async (data: {
  boxID: string;
  name: string;
  description?: string | null;
  capacity: number;
  duration: number;
  color?: string | null;
  allowedMembershipProductIDs?: string[] | null;
  workoutProgramUIDs?: string[] | null;
  isInternalByDefault?: boolean;
  // Absent = the server's own default; null = an explicit "no room" (resolveRoomID).
  roomID?: string | null;
}): Promise<{ classTypeID: string; success: boolean }> => {
  const createClassTypeFn = httpsCallable(firebaseFunctions, "createClassType");

  // Normalize allowedMembershipProductIDs: null, undefined, or empty array = "all members"
  const normalizedMembershipIDs =
    !data.allowedMembershipProductIDs || data.allowedMembershipProductIDs.length === 0
      ? null
      : data.allowedMembershipProductIDs;

  const result = await createClassTypeFn({
    ...data,
    allowedMembershipProductIDs: normalizedMembershipIDs,
    isInternalByDefault: data.isInternalByDefault ?? false, // Default to false if not provided
  });

  return result.data as { classTypeID: string; success: boolean };
};

/**
 * Update a class type
 */
export const updateClassType = async (data: {
  boxID: string;
  classTypeID: string;
  updateData: Partial<Omit<ClassType, "id" | "createdAt" | "updatedAt">>;
}): Promise<{ success: boolean }> => {
  const updateClassTypeFn = httpsCallable(firebaseFunctions, "updateClassType");
  const result = await updateClassTypeFn(data);
  return result.data as { success: boolean };
};

/**
 * Delete (soft delete) a class type
 */
export const deleteClassType = async (data: {
  boxID: string;
  classTypeID: string;
}): Promise<{ success: boolean }> => {
  const deleteClassTypeFn = httpsCallable(firebaseFunctions, "deleteClassType");
  const result = await deleteClassTypeFn(data);
  return result.data as { success: boolean };
};

/**
 * Turn a soft-deleted class type back on (admins only). Turning it off stays
 * with deleteClassType.
 */
export const reactivateClassType = async (
  data: ReactivateClassTypeRequest
): Promise<ReactivateClassTypeResponse> => {
  const reactivateClassTypeFn = httpsCallable(firebaseFunctions, "reactivateClassType");
  const result = await reactivateClassTypeFn(data);
  return result.data as ReactivateClassTypeResponse;
};

/**
 * Get all class types for a box. `includeInactive` also returns turned-off
 * types, for box admins only; the server ignores it for everyone else.
 */
export const getClassTypes = async (
  data: GetClassTypesRequest
): Promise<{ classTypes: Array<ClassType & { id: string }> }> => {
  const getClassTypesFn = httpsCallable(firebaseFunctions, "getClassTypes");
  const result = await getClassTypesFn(data);
  return result.data as { classTypes: Array<ClassType & { id: string }> };
};

/**
 * Get a single class type by ID
 */
export const getClassType = async (data: {
  boxID: string;
  classTypeID: string;
}): Promise<{ classType: ClassType & { id: string } }> => {
  const getClassTypeFn = httpsCallable(firebaseFunctions, "getClassType");
  const result = await getClassTypeFn(data);
  return result.data as { classType: ClassType & { id: string } };
};

/* =======================================
 * Class Scheduling Functions
 * ====================================== */

/**
 * Create a new class
 */
export const createClass = async (data: {
  boxID: string;
  classTypeID: string;
  coachUID?: string; // Optional - class can be created without a coach assigned
  startTime: Date | string;
  notes?: string | null;
  isInternal?: boolean;
  // Absent = inherit the class type's default; null = an explicit "no room".
  roomID?: string | null;
}): Promise<{ classID: string; success: boolean }> => {
  const createClassFn = httpsCallable(firebaseFunctions, "createClass");
  const result = await createClassFn(data);
  return result.data as { classID: string; success: boolean };
};

/**
 * Update a class
 */
export const updateClass = async (data: UpdateClassRequest): Promise<UpdateClassResponse> => {
  // A moved class notifies every booked athlete inside the call, up to the function's 300s timeout; the SDK default is 70s.
  const updateClassFn = httpsCallable<UpdateClassRequest, UpdateClassResponse>(
    firebaseFunctions,
    "updateClass",
    { timeout: 300000 }
  );
  const result = await updateClassFn(data);
  return result.data;
};

/**
 * Cancel a class: keeps it on the schedule as cancelled, refunds visit cards
 * and notifies the athletes. The legacy deleteClass callable still exists for
 * older dashboard bundles, but no wrapper is exported for it: deleting a class
 * destroys its attendance record and nothing here should reach for it.
 */
export const cancelClass = async (data: CancelClassRequest): Promise<CancelClassResponse> => {
  const cancelClassFn = httpsCallable(firebaseFunctions, "cancelClass");
  const result = await cancelClassFn(data);
  return result.data as CancelClassResponse;
};

/**
 * Get classes for a box with filters
 */
export const getClasses = async (data: {
  boxID: string;
  startDate?: Date | string;
  endDate?: Date | string;
  coachUID?: string;
  classTypeID?: string;
  status?: "scheduled" | "completed" | "cancelled";
  limit?: number;
}): Promise<{ classes: Array<Class & { id: string }> }> => {
  const getClassesFn = httpsCallable(firebaseFunctions, "getClasses");
  // Backend expects filters to be wrapped in a filters object
  const result = await getClassesFn({
    boxID: data.boxID,
    filters: {
      startDate: data.startDate,
      endDate: data.endDate,
      coachUID: data.coachUID,
      classTypeID: data.classTypeID,
      status: data.status,
      limit: data.limit,
    },
  });
  return result.data as { classes: Array<Class & { id: string }> };
};

/**
 * Get class details including bookings and waitlist
 */
export const getClassDetails = async (data: {
  boxID: string;
  classID: string;
}): Promise<{
  class: Class & { id: string };
  bookings: Array<ClassBooking & { id: string }>;
  waitlist: Array<ClassWaitlist & { id: string }>;
}> => {
  const getClassDetailsFn = httpsCallable(firebaseFunctions, "getClassDetails");
  const result = await getClassDetailsFn(data);
  return result.data as {
    class: Class & { id: string };
    bookings: Array<ClassBooking & { id: string }>;
    waitlist: Array<ClassWaitlist & { id: string }>;
  };
};

/**
 * Assign coach to a class
 */
export const assignCoach = async (data: {
  boxID: string;
  classID: string;
  coachUID: string;
}): Promise<{ success: boolean }> => {
  const assignCoachFn = httpsCallable(firebaseFunctions, "assignCoach");
  const result = await assignCoachFn(data);
  return result.data as { success: boolean };
};

/* =======================================
 * Booking Management Functions
 * ====================================== */

/**
 * Book a class.
 *
 * An idempotency token is generated per invocation and passed to the
 * callable. A gRPC retry or transient network failure on the same call
 * replays through the same token; the backend returns the original
 * bookingID without creating a duplicate booking. Each fresh user tap
 * generates a new token (the in-flight UX guard prevents the duplicate
 * tap from reaching here in the first place).
 */
export const bookClass = async (data: {
  boxID: string;
  classID: string;
}): Promise<{ success: boolean; bookingID: string; message: string }> => {
  const bookClassFn = httpsCallable(firebaseFunctions, "bookClass");
  const result = await bookClassFn({ ...data, idempotencyToken: uuidv4() });
  return result.data as { success: boolean; bookingID: string; message: string };
};

/**
 * Cancel a booking. See bookClass for idempotency-token semantics.
 */
export const cancelBooking = async (data: {
  boxID: string;
  bookingID: string;
  reason?: string | null;
}): Promise<{ success: boolean; message: string }> => {
  const cancelBookingFn = httpsCallable(firebaseFunctions, "cancelBooking");
  const result = await cancelBookingFn({ ...data, idempotencyToken: uuidv4() });
  return result.data as { success: boolean; message: string };
};

/**
 * Staff book a member into a class (F032 D22). The member's own entitlement is
 * debited. A refusal carries `details.reason`; "class_full" and
 * "booking_limit" can be retried with the matching override.
 */
export const staffBookMember = async (
  boxID: string,
  classID: string,
  athleteUID: string,
  overrides: { allowOverCapacity?: boolean; allowOverLimit?: boolean } = {}
): Promise<BookClassResponse> => {
  const request: BookClassRequest = {
    boxID,
    classID,
    athleteUID,
    ...overrides,
    idempotencyToken: uuidv4(),
  };
  const bookClassFn = httpsCallable(firebaseFunctions, "bookClass");
  const result = await bookClassFn(request);
  const data = result.data as BookClassResponse;
  // A server from before F032 ignores athleteUID and books the caller instead.
  if (data.onBehalf !== true) {
    throw Object.assign(new Error("bookClass did not book on behalf of the member"), {
      userMessage:
        "The server booked you instead of the member. Cancel that booking and try again after the update.",
      bookingID: data.bookingID,
    });
  }
  return data;
};

/**
 * Staff cancel a member's booking (F032 D22). No 1-hour rule; `refundVisit`
 * decides whether a spent visit goes back on the card.
 */
export const staffCancelBooking = async (
  boxID: string,
  bookingID: string,
  athleteUID: string,
  { refundVisit }: { refundVisit: boolean }
): Promise<CancelBookingResponse> => {
  const request: CancelBookingRequest = {
    boxID,
    bookingID,
    athleteUID,
    refundVisit,
    idempotencyToken: uuidv4(),
  };
  const cancelBookingFn = httpsCallable(firebaseFunctions, "cancelBooking");
  const result = await cancelBookingFn(request);
  return result.data as CancelBookingResponse;
};

/**
 * Staff move a member's booking to another class (F032 D23). The membership
 * that paid for it must cover the target; nothing is charged or refunded. A
 * refusal carries `details.reason`; only "class_full" can be retried, with
 * `allowOverCapacity`.
 */
export const staffMoveBooking = async (
  boxID: string,
  bookingID: string,
  athleteUID: string,
  toClassID: string,
  overrides: { allowOverCapacity?: boolean } = {}
): Promise<MoveBookingResponse> => {
  const request: MoveBookingRequest = {
    boxID,
    bookingID,
    athleteUID,
    toClassID,
    ...overrides,
    idempotencyToken: uuidv4(),
  };
  const moveBookingFn = httpsCallable(firebaseFunctions, "moveBooking");
  const result = await moveBookingFn(request);
  const data = result.data as MoveBookingResponse;
  if (data.onBehalf !== true) {
    throw Object.assign(new Error("moveBooking did not confirm the move"), {
      userMessage:
        "The server could not confirm the move. Refresh the class to see where the member is booked.",
      bookingID: data.bookingID,
    });
  }
  return data;
};

/**
 * Check in for a class. See bookClass for idempotency-token semantics.
 */
export const checkIn = async (data: {
  boxID: string;
  bookingID: string;
  athleteUID?: string; // Optional, for coach/admin checking in others
}): Promise<{ success: boolean; message: string }> => {
  const checkInFn = httpsCallable(firebaseFunctions, "checkIn");
  const result = await checkInFn({ ...data, idempotencyToken: uuidv4() });
  return result.data as { success: boolean; message: string };
};

/**
 * Get athlete's bookings (paginated, cursor-based).
 *
 * - `limit` defaults to 20 server-side and is hard-capped at 100.
 * - `cursor` is an opaque string returned as `nextCursor` from the previous call;
 *   pass it back to fetch the next page. Omit to fetch the first page.
 * - `nextCursor` is null on the last page.
 */
export const getMyBookings = async (data: {
  boxID: string;
  status?: "booked" | "checked_in" | "cancelled" | "no_show";
  startDate?: Date | string;
  endDate?: Date | string;
  limit?: number;
  cursor?: string | null;
}): Promise<{
  bookings: Array<
    ClassBooking & {
      id: string;
      class: Class & { id: string };
    }
  >;
  // Optional rather than required: a pre-pagination backend deploy will
  // simply omit this field, and callers using `?? null` continue to work.
  nextCursor?: string | null;
}> => {
  const getMyBookingsFn = httpsCallable(firebaseFunctions, "getMyBookings");
  const result = await getMyBookingsFn(data);
  return result.data as {
    bookings: Array<
      ClassBooking & {
        id: string;
        class: Class & { id: string };
      }
    >;
    nextCursor?: string | null;
  };
};

/**
 * Get bookings for a class (Coach/Admin only), paginated cursor-based.
 *
 * - `limit` defaults to 20 server-side and is hard-capped at 100.
 * - `cursor` is an opaque string returned as `nextCursor` from the previous
 *   call; pass it back to fetch the next page. Omit to fetch the first page.
 * - `nextCursor` is null on the last page.
 */
export const getClassBookings = async (data: {
  boxID: string;
  classID: string;
  limit?: number;
  cursor?: string | null;
}): Promise<{
  bookings: Array<ClassBooking & { id: string }>;
  class: Class & { id: string };
  // Optional rather than required: a pre-pagination backend deploy will simply
  // omit this field, and callers using `?? null` continue to work.
  nextCursor?: string | null;
}> => {
  const getClassBookingsFn = httpsCallable(firebaseFunctions, "getClassBookings");
  const result = await getClassBookingsFn(data);
  return result.data as {
    bookings: Array<ClassBooking & { id: string }>;
    class: Class & { id: string };
    nextCursor?: string | null;
  };
};

/* =======================================
 * Waitlist Management Functions
 * ====================================== */

/**
 * Join waitlist for a class. See bookClass for idempotency-token semantics.
 */
export const joinWaitlist = async (data: {
  boxID: string;
  classID: string;
}): Promise<{
  success: boolean;
  waitlistID: string;
  position: number;
  message: string;
}> => {
  const joinWaitlistFn = httpsCallable(firebaseFunctions, "joinWaitlist");
  const result = await joinWaitlistFn({ ...data, idempotencyToken: uuidv4() });
  return result.data as {
    success: boolean;
    waitlistID: string;
    position: number;
    message: string;
  };
};

/**
 * Promote next person from waitlist (active staff of the box only, LT-1806).
 * The response no longer names the promoted athlete's uid.
 */
export const promoteFromWaitlist = async (data: {
  boxID: string;
  classID: string;
}): Promise<PromoteFromWaitlistResponse> => {
  const promoteFromWaitlistFn = httpsCallable(firebaseFunctions, "promoteFromWaitlist");
  const result = await promoteFromWaitlistFn(data);
  return result.data as PromoteFromWaitlistResponse;
};

/**
 * Remove from waitlist. See bookClass for idempotency-token semantics.
 */
export const removeFromWaitlist = async (data: {
  boxID: string;
  waitlistID: string;
  athleteUID?: string; // Optional, for admin/coach removing others
}): Promise<{ success: boolean; message: string }> => {
  const removeFromWaitlistFn = httpsCallable(firebaseFunctions, "removeFromWaitlist");
  const result = await removeFromWaitlistFn({ ...data, idempotencyToken: uuidv4() });
  return result.data as { success: boolean; message: string };
};

/**
 * Get waitlist for a class (Coach/Admin only), paginated cursor-based.
 *
 * - `limit` defaults to 20 server-side and is hard-capped at 100.
 * - `cursor` is an opaque string returned as `nextCursor` from the previous
 *   call; pass it back to fetch the next page. Omit to fetch the first page.
 * - `nextCursor` is null on the last page.
 */
export const getWaitlist = async (data: {
  boxID: string;
  classID: string;
  limit?: number;
  cursor?: string | null;
}): Promise<{
  waitlist: Array<ClassWaitlist & { id: string }>;
  class: Class & { id: string };
  nextCursor?: string | null;
}> => {
  const getWaitlistFn = httpsCallable(firebaseFunctions, "getWaitlist");
  const result = await getWaitlistFn(data);
  return result.data as {
    waitlist: Array<ClassWaitlist & { id: string }>;
    class: Class & { id: string };
    nextCursor?: string | null;
  };
};

/**
 * Who is booked into a class, as another member may see them (F025). The server
 * decides visibility per caller; `enabled: false` means show no classmate UI.
 */
export const getClassmates = async (data: GetClassmatesRequest): Promise<GetClassmatesResponse> => {
  const getClassmatesFn = httpsCallable<GetClassmatesRequest, GetClassmatesResponse>(
    firebaseFunctions,
    "getClassmates"
  );
  const result = await getClassmatesFn(data);
  return result.data;
};
